import type { BaseTheme } from "common/BaseTheme";
import type { CoreTheme } from "sharedUI/styles";
import { coreTheme } from "sharedUI/styles";
import { lookupValue } from "../../theme-util";
import { fontStyles } from "../font-styles";

export const pageMarginMedium = 16;

// Runtime themes are the actual themed used at runtime. This includes computed values and
// values which should not be changed by the app or values which are set based on the current
// target platform.
export type RuntimeTheme = typeof defaultRuntimeTheme & BaseTheme;

const shadowColor = (opacity: number) => `rgba(62, 65, 86, ${opacity})`;

const appCoreTheme: CoreTheme = {
    ...coreTheme,

    n0: "#FFFFFF",
    n0A: "rgba(255, 255, 255, 0)",
    n100: "#F6F6F6",
    n100A: "rgba(121, 121, 121, 0.06)",
    n200: "#F2F2F2",
    n200A: "rgba(121, 121, 121, 0.1)",

    n300: "#E8E8E8",
    n300A: "rgba(121, 121, 121, 0.16)",
    n400: "#E1E1E1",
    n400A: "rgba(116, 116, 116, 0.22)",
    n500: "#B9B9B9",
    n500A: "rgba(65, 65, 65, 0.37)",

    n600: "#999999",
    n600A: "rgba(45, 45, 45, 0.58)",
    n700: "#555555",
    n700A: "rgba(35, 35, 35, 0.8)",
    n800: "#111111",
    n800A: "rgba(29, 29, 29, 0.92)",
    n900: "#000000",

    textDark: lookupValue("n900"),
    textBase: lookupValue("n800"),
    textPale: lookupValue("n700"),
    textXpale: lookupValue("n600"),
    textDisabled: lookupValue("n500"),

    iconDark: lookupValue("n800"),
    iconBase: lookupValue("n700"),
    iconPale: lookupValue("n600"),
    iconXpale: lookupValue("n500"),
    iconDisabled: lookupValue("n400"),

    accent: lookupValue("primaryAccentColor"),

    shadowSoft0: `0px 1px 0px ${shadowColor(0.04)}`,
    shadowSoft1: `box-shadow: 0px 1px 3px ${shadowColor(0.08)};`,
    shadowSoft2: `0px 6px 12px ${shadowColor(0.1)}`,
    shadowSoft3: `0px 10px 20px ${shadowColor(0.1)}`,
    shadowSoft4: `0px 24px 48px ${shadowColor(0.1)}`,

    shadow0: `0px 1px 0px ${shadowColor(0.06)}`,
    shadow1: `0px 0px 0px 1px ${shadowColor(0.06)}, 0px 1px 3px ${shadowColor(0.08)}`,
    shadow2: `0px 0px 0px 1px ${shadowColor(0.06)}, 0px 6px 12px ${shadowColor(0.08)}`,
    shadow3: `0px 0px 1px 1px ${shadowColor(0.06)}, 0px 10px 20px ${shadowColor(0.08)}`,
    shadow4: `0px 0px 1px 1px ${shadowColor(0.06)}, 0px 24px 48px ${shadowColor(0.08)}`,

    shadowDark0: `0px 1px 0px ${shadowColor(0.12)}`,
    shadowDark1: `0px 1px 1px rgba(0, 0, 0, 0.06), 0px 0px 0px 1px ${shadowColor(0.08)}, 0px 1px 3px ${shadowColor(
        0.08
    )}`,
    shadowDark2: `0px 0px 0px 1px ${shadowColor(0.08)}, 0px 2px 4px rgba(0, 0, 0, 0.06), 0px 6px 12px ${shadowColor(
        0.08
    )}`,
    shadowDark3: `0px 0px 0px 1px ${shadowColor(0.08)}, 0px 3px 6px rgba(0, 0, 0, 0.06), 0px 10px 20px ${shadowColor(
        0.1
    )}`,
    shadowDark4: `0px 0px 0px 1px ${shadowColor(0.08)}, 0px 4px 8px rgba(0, 0, 0, 0.06), 0px 24px 48px ${shadowColor(
        0.12
    )}`,

    shadowXdark0: `0px 1px 0px ${shadowColor(0.25)}`,
    shadowXdark1: `0px 1px 1px rgba(0, 0, 0, 0.06), 0px 0px 0px 1px ${shadowColor(0.08)}, 0px 1px 3px ${shadowColor(
        0.25
    )}`,
    shadowXdark2: `0px 0px 0px 1px ${shadowColor(0.08)}, 0px 2px 4px rgba(0, 0, 0, 0.06), 0px 4px 12px ${shadowColor(
        0.25
    )}`,
    shadowXdark3: `0px 0px 0px 1px ${shadowColor(0.08)}, 0px 3px 6px rgba(0, 0, 0, 0.06), 0px 10px 20px ${shadowColor(
        0.25
    )}`,
    shadowXdark4: `0px 6px 12px rgba(0, 0, 0, 0.06), 0px 0px 0px 1px ${shadowColor(0.08)}, 0px 24px 48px ${shadowColor(
        0.25
    )}`,
};

export const defaultRuntimeTheme = {
    overlayName: "default",

    ...appCoreTheme,
    // start of note: These aren't in the appCoreTheme because we want to stop using them in favor of textDark, textBase
    fgColorOffBase: lookupValue("n600"),
    fgColorLight: lookupValue("n600"),
    fgColorMedium: lookupValue("n700"),
    fgColorDark: lookupValue("n800"),
    fgColorOpaque: lookupValue("n900"),
    baseColor: lookupValue("n0"),
    offBaseColor: lookupValue("n100"),
    skeletonColor: lookupValue("n200"),
    linkColor: "#106ba3",
    separatorColor: "rgba(0, 0, 0, 0.12)",
    veilColor: "rgba(0, 0, 0, 0.2)",
    destroyColor: lookupValue("r500"),

    primaryOverlayColor: "#FFFFFFF9",
    transparentOverlayColor: "#FFFFFFC9",
    accentForegroundColor: lookupValue("n0"),
    // end of note

    builderWidth: 375,
    builderHeight: 812,

    pageMarginMedium,
    componentInnerVerticalPadding: 12,
    componentSmallInnerVerticalPadding: 8,

    preferredRoundingRadius: 10,

    fontStyles,

    mapTheme: "light",
    avoidColors: [
        { color: "#FFFFFF", contrast: 1.8, isBase: true },
        { color: "#000000", contrast: 1.7, isBase: false },
    ],

    textField: {
        underlineColor: "rgba(0, 0, 0, 0.15)",
    },

    calendar: {
        pastEventColor: lookupValue("n300"),
        pastEventAccent: lookupValue("n600"),
    },

    statusBar: {
        color: "#000",
        height: 44,
    },

    homeBar: {
        color: "#000",
        height: 34,
    },
    expandableOptions: {
        modal: {
            bgColor: "#FFFFFF",
        },
        options: {
            bgColorSelected: "_primaryAccentColor",
            bgColorUnselected: "#f5f5f5",
            fgColorSelected: "#ffffff",
            fgColorUnselected: "#c4c4c4",
        },
        button: {
            bgColorSelected: "_primaryAccentColor",
            bgColorUnselected: "#f5f5f5",
            fgColorSelected: "#ffffff",
            fgColorUnselected: "#c4c4c4",
        },
    },
    cards: {
        bgColor: "_baseColor",
    },
    navBar: {
        height: 44,
        heightWithSearch: 96,
        titleFontStyle: lookupValue<any>("fontStyles.AccentCallout4Fixed"),
        style: "iOS",
        fgColor: "_primaryAccentColor",
        titleColor: "_fgColorDark",
        bgColor: "_primaryOverlayColor",
        transparentBgColor: "_transparentOverlayColor",
        fadeTo: "white",
        cancelColor: "_fgColorDark",

        largeTitleHeight: 0,
        borderRadius: 0,
    },
    offlineBanner: {
        height: 64,
        gap: 12,
    },

    scrollView: {
        backgroundColor: "none",
        marginTop: 0,
        marginLeft: 0,
        borderRadius: 0,
        paddingTop: 0,
        paddingLeft: 0,
    },

    tabBar: {
        height: 49,
        unselectedColor: "#999999",

        tab: {
            phone: {
                fontStyle: lookupValue<any>("fontStyles.Subheader7Fixed"),
            },
            tablet: {
                fontStyle: lookupValue<any>("fontStyles.Subheader6Fixed"),
            },
        },
    },

    picker: {
        backgroundColor: "rgba(0, 0, 0, 0.05)",
        hoverBgColor: "rgba(0, 0, 0, 0.075)",
        activeBgColor: "rgba(0, 0, 0, 0.12)",
        currentBgColor: "_baseColor",
        currentColor: "_primaryAccentColor",
    },

    materialDateTimePicker: {
        borderRadius: "12",
        borderWidth: "0",
        borderColor: "transparent",
        borderStyle: "none",

        header: {
            background: "_baseColor",
            textDark: "_fgColorDark",
            textLight: "_fgColorLight",
        },
        tabs: {
            background: "_baseColor",
            iconColor: "_fgColorMedium",
            iconSelectedColor: "_primaryAccentColor",
        },
        cancelConfirm: {
            bg: "_primaryAccentColor",
            fg: "_accentForegroundColor",
        },
    },

    searchBar: {
        style: "Inline",
        height: 36,
        heightWithPadding: 52, // convenience mostly
        paddingSides: 12,
        iconPadding: 8,
        fgColor: "_fgColorLight",
        placeholderColor: "_fgColorLight",
        textColor: "_fgColorDark",
        backgroundColor: "rgba(0, 0, 0, 0.05)",
        maxWidth: "100%",
    },

    signature: {
        invert: false,
    },

    pivot: {
        style: "SplitButton",
        height: 32,
        bottomMargin: lookupValue<number>("pageMarginMedium"),
        troughColor: lookupValue("n200A"),
        troughFg: lookupValue("textDark"),
    },

    gridItem: {
        title: {
            largeFontStyle: lookupValue<any>("fontStyles.AccentCallout2"),
            fontStyle: lookupValue<any>("fontStyles.AccentCallout4"),
            smallFontStyle: lookupValue<any>("fontStyles.AccentCallout5"),
        },
        subtitle: {
            largeFontStyle: lookupValue<any>("fontStyles.Header4"),
            fontStyle: lookupValue<any>("fontStyles.Header5"),
            smallFontStyle: lookupValue<any>("fontStyles.Header6"),
        },
    },

    list: {
        topMargin: 0,
    },

    listItem: {
        defaultRowHeight: 72,
        titleFontStyle: lookupValue<any>("fontStyles.Header4"),
        titleLineHeight: lookupValue<number>("fontStyles.Header4Height"),
        detailFontStyle: lookupValue<any>("fontStyles.Caption5"),
        detailLineHeight: lookupValue<number>("fontStyles.Caption5Height"),
        captionFontStyle: lookupValue<any>("fontStyles.Body6"),
        captionLineHeight: lookupValue<number>("fontStyles.Body6Height"),
        captionAlign: "inherit",
        selectedColor: "rgba(0, 0, 0, 0.05)",
        selectedColorOpaque: "rgba(235, 235, 235, 1)",
        textDarkenAmount: 25,
        underlineLeft: lookupValue<number>("pageMarginMedium"),
        underlineRight: 0,
        initialsFontStyle: lookupValue<any>("fontStyles.AccentCallout2Fixed"),

        mainImage: {
            width: 48,
            height: 48,
            borderRadius: "50%",
            marginRight: 10,
        },
        actionImage: {
            width: 40,
            height: 40,
            borderRadius: "50%",
            iconSize: 18,
            margin: 10,
            color: "#fff",
        },
    },

    tileItem: {
        padding: 0,
        borderRadius: 6,
        selectedColor: "none",
    },

    summary: {
        height: 300,

        title: {
            fontStyle: lookupValue<any>("fontStyles.AccentHeader3"),
        },
        detail: {
            fontStyle: lookupValue<any>("fontStyles.AccentCallout4"),
        },
    },

    youtube: {
        defaultHeight: 300,
    },

    map: {
        defaultHeight: 300,
        locationButtonForegroundColor: "_primaryAccentColor",
    },

    switch: {
        compact: {
            backgroundColor: "#000000",
            thumbColor: "#ffffff",
        },
        big: {
            selectedBackgroundColor: "_primaryAccentColor",
            backgroundColor: "_picker.hoverBgColor",
            thumbColor: "#ffffff",
            borderColor: "_picker.backgroundColor",
        },
    },

    button: {
        filledTextColor: "white",
        borderRadius: 10,
        height: 44,
        uppercase: false,
        shadow: "unset",
        fontStyle: lookupValue<any>("fontStyles.Subheader4"),
    },

    image: {
        caption: {
            color: "#FFFFFF",
            fontSize: 12,
            margin: 10,
            borderRadius: 20,
            opacity: 0.8,
            paddingVertical: 6,
            paddingHorizontal: 10,
            backgroundColor: "#000000",
        },
    },

    enlargedImageOverlay: {
        backgroundColor: "#000000",
        textColor: "#ffffff",
    },

    formSubmissionSuccessToast: {
        background: "rgba(230, 230, 230, 0.96)",
        translucentBackground: "rgba(230, 230, 230, 0.6)",
        checkmark: "black",
        labelColor: "#000000",
    },

    flyout: {
        overlayColor: "#ffffff99",
        bgColor: "white",
        headerFg: "rgba(0, 0, 0, 0.9)",
        headerBg: "white",
        headerBorderColor: "#E0E0E0",
        rightBorderColor: "#E0E0E0",
        headerImgColor: "#E8E8E8",
    },

    userProfile: {
        title: {
            textColor: "_fgColorOpaque",
            fontStyle: lookupValue<any>("fontStyles.AccentHeader3"),
            marginBottom: 4,
        },
        subtitle: {
            textColor: "_fgColorMedium",
            fontStyle: lookupValue<any>("fontStyles.Body4"),
        },
    },

    quotaBar: {
        height: 60,
        bgColor: "_offBaseColor",
    },
};
